import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationPrefsInput, ProfileInput } from '@auditwire/contracts';
import { queryKeys } from '@/shared/config/queryKeys';
import { useAuth } from '@/features/auth';
import { profileRepository } from './profileRepository';

/** The current user's profile. */
export function useProfile() {
  const { user } = useAuth();
  const userId = user?.id;
  return useQuery({
    queryKey: userId ? queryKeys.profile(userId) : ['profile', 'anon'],
    queryFn: () => profileRepository.get(userId ?? ''),
    enabled: Boolean(userId),
  });
}

export function useUpdateProfile() {
  const { user, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;
  return useMutation({
    mutationFn: (input: ProfileInput) => profileRepository.update(userId ?? '', input),
    onSuccess: async (profile) => {
      if (userId) queryClient.setQueryData(queryKeys.profile(userId), profile);
      // Refresh the AuthUser so the header avatar/name reflect the change.
      await refreshProfile();
    },
  });
}

export function useUploadProfilePicture() {
  const { user, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;
  return useMutation({
    mutationFn: ({ file, previousUrl }: { file: File; previousUrl: string | null }) =>
      profileRepository.uploadProfilePicture(userId ?? '', file, previousUrl),
    onSuccess: async (profile) => {
      if (userId) queryClient.setQueryData(queryKeys.profile(userId), profile);
      await refreshProfile();
    },
  });
}

export function useRemoveProfilePicture() {
  const { user, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;
  return useMutation({
    mutationFn: (currentUrl: string | null) =>
      profileRepository.removeProfilePicture(userId ?? '', currentUrl),
    onSuccess: async (profile) => {
      if (userId) queryClient.setQueryData(queryKeys.profile(userId), profile);
      await refreshProfile();
    },
  });
}

export function useNotificationPrefs() {
  const { user } = useAuth();
  const userId = user?.id;
  return useQuery({
    queryKey: userId ? queryKeys.notificationPrefs(userId) : ['notificationPrefs', 'anon'],
    queryFn: () => profileRepository.getNotificationPrefs(userId ?? ''),
    enabled: Boolean(userId),
  });
}

export function useUpdateNotificationPrefs() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;
  return useMutation({
    mutationFn: (input: NotificationPrefsInput) =>
      profileRepository.updateNotificationPrefs(userId ?? '', input),
    onSuccess: (prefs) => {
      if (userId) queryClient.setQueryData(queryKeys.notificationPrefs(userId), prefs);
    },
  });
}

export function useUpdateAccount() {
  return useMutation({
    mutationFn: (changes: { email?: string; password?: string }) =>
      profileRepository.updateAccount(changes),
  });
}
